import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { requestLoginCode, verifyLoginCode, demoLogin, devAutoLogin, logoutFromServer } from '@/api/salesRepApi'
import { setSessionToken, clearSession, getSessionToken, getDevAutoLoginEmail, isDevelopment } from '@/api/config'
import type { LoginResponse, SalesRepInfo } from '@/types'

const SESSION_REP_KEY = 'salesRepApp_salesRep'

export const useAuthStore = defineStore('auth', () => {
  // ── State ────────────────────────────────────────────────────────────────
  const salesRep = ref<SalesRepInfo | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  /** Populated when login fails because the floating license is fully checked out */
  const licenseLimitInfo = ref<{
    activeUsers: number
    maxUsers: number
    userHasExistingSession: boolean
    userExistingSessionCount: number
  } | null>(null)

  // Restore session from sessionStorage on store init
  const storedRep = sessionStorage.getItem(SESSION_REP_KEY)
  if (storedRep) {
    try {
      salesRep.value = JSON.parse(storedRep) as SalesRepInfo
    } catch {
      sessionStorage.removeItem(SESSION_REP_KEY)
    }
  }

  // ── Computed ─────────────────────────────────────────────────────────────
  // salesRep.value provides a reactive dependency so Vue re-evaluates this
  // computed when login/logout changes the store state. Without it, the computed
  // would only read the non-reactive sessionStorage and never update.
  const isAuthenticated = computed(() => !!salesRep.value && !!getSessionToken())
  const repName = computed(() => salesRep.value?.name ?? '')
  const repEmail = computed(() => salesRep.value?.email ?? '')
  const isAdminRep = computed(() => salesRep.value?.isAdmin ?? false)
  const canCreatePayment = computed(() => salesRep.value?.canCreatePayment ?? false)
  const canEditAccount = computed(() => salesRep.value?.canEditAccount ?? false)
  const canCreateAccount = computed(() => salesRep.value?.canCreateAccount ?? false)

  // ── Actions ──────────────────────────────────────────────────────────────

  /**
   * Step 1: Request a verification code.
   * The backend looks up the email in the tenant's AccountScout database,
   * confirms the user has a valid license, and emails a code.
   */
  async function sendVerificationCode(email: string, captchaToken?: string): Promise<LoginResponse> {
    isLoading.value = true
    error.value = null
    try {
      const result = await requestLoginCode(email, captchaToken)
      if (!result.success || !result.emailSent) {
        error.value = result.message ?? 'Failed to send verification code.'
        return {
          success: false,
          message: error.value,
          emailSent: false
        }
      }
      return result
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'An error occurred.'
      return {
        success: false,
        message: error.value,
        emailSent: false
      }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Step 2: Verify the code and establish a session.
   */
  async function verifyCode(email: string, code: string, forceLogin?: boolean): Promise<boolean> {
    isLoading.value = true
    error.value = null
    licenseLimitInfo.value = null
    try {
      const result = await verifyLoginCode(email, code, forceLogin)
      if (result.licenseLimitReached) {
        licenseLimitInfo.value = {
          activeUsers: result.activeUsers ?? 0,
          maxUsers: result.maxUsers ?? 0,
          userHasExistingSession: result.userHasExistingSession ?? false,
          userExistingSessionCount: result.userExistingSessionCount ?? 0
        }
        error.value = result.message ?? 'License limit reached.'
        return false
      }
      if (!result.success || !result.sessionToken) {
        error.value = result.message ?? 'Invalid or expired code.'
        return false
      }
      setSessionToken(result.sessionToken)
      if (result.salesRep) {
        salesRep.value = result.salesRep
        sessionStorage.setItem(SESSION_REP_KEY, JSON.stringify(result.salesRep))
      }
      return true
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'An error occurred.'
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function logout(): Promise<void> {
    await logoutFromServer()
    salesRep.value = null
    error.value = null
    licenseLimitInfo.value = null
    clearSession()
  }

  /**
   * Demo login — skips OTP, directly creates a session.
   * Used by internal team for demonstrations.
   */
  async function demoSignIn(email: string, demoSecret: string): Promise<boolean> {
    isLoading.value = true
    error.value = null
    licenseLimitInfo.value = null
    try {
      const result = await demoLogin(email, demoSecret)
      if (!result.success || !result.sessionToken) {
        error.value = result.message ?? 'Demo login failed.'
        return false
      }
      setSessionToken(result.sessionToken)
      if (result.salesRep) {
        salesRep.value = result.salesRep
        sessionStorage.setItem(SESSION_REP_KEY, JSON.stringify(result.salesRep))
      }
      return true
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'An error occurred.'
      return false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Dev-only: automatically log in without OTP.
   * Only works when the API is running on localhost.
   */
  async function tryDevAutoLogin(email?: string): Promise<boolean> {
    if (!isDevelopment()) return false
    const targetEmail = email?.trim() || getDevAutoLoginEmail()
    try {
      const result = await devAutoLogin(targetEmail)
      if (!result.success || !result.sessionToken) return false
      setSessionToken(result.sessionToken)
      if (result.salesRep) {
        salesRep.value = result.salesRep
        sessionStorage.setItem(SESSION_REP_KEY, JSON.stringify(result.salesRep))
      }
      console.log(`[auth] Dev auto-login successful for ${targetEmail}`)
      return true
    } catch {
      console.log(`[auth] Dev auto-login not available for ${targetEmail}`)
      return false
    }
  }

  function clearError(): void {
    error.value = null
    licenseLimitInfo.value = null
  }

  return {
    salesRep,
    isLoading,
    error,
    licenseLimitInfo,
    isAuthenticated,
    repName,
    repEmail,
    isAdminRep,
    canCreatePayment,
    canEditAccount,
    canCreateAccount,
    sendVerificationCode,
    verifyCode,
    demoSignIn,
    tryDevAutoLogin,
    logout,
    clearError
  }
})
